import { PlantSchema } from "@/app/shared/services/volateq-api/api-schemas/plant-schema";
import Vue from "vue";
import { RawLocation } from "vue-router";
import { PlantRouteQuery } from "../types";
import { EventEmitter } from "events";

export class RouteQueryHelper {
  private static event = new EventEmitter();

  public static emitQueryChanged() {
    RouteQueryHelper.event.emit("queryChanged");
  }

  private listeners: Array<(event: any) => Promise<void>> = [];

  constructor(private readonly vueComponent: Vue & { plant: PlantSchema }) {}

  public queryChanged(onQueryChanged: () => Promise<void>) {
    const listener = async (event: any) => {
      await onQueryChanged();
    };

    window.addEventListener("popstate", listener);

    RouteQueryHelper.event.on("queryChanged", listener);

    this.listeners.push(listener);
  }

  public async pushRoute(query: PlantRouteQuery) {
    if (this.hasQueryChanged(query)) {
      await this.vueComponent.$router.push(this.getRoute(query));

      RouteQueryHelper.emitQueryChanged();
    }
  }

  public async replaceRoute(query: PlantRouteQuery) {
    if (this.hasQueryChanged(query)) {
      await this.vueComponent.$router.replace(this.getRoute(query));
    }
  }

  public unregister() {
    for (const listener of this.listeners) {
      RouteQueryHelper.event.removeListener("queryChanged", listener);
      window.removeEventListener("popstate", listener);
    }
  }

  private hasQueryChanged(query: PlantRouteQuery): boolean {
    for (const key of Object.keys(query)) {
      if (!this.isEqual(query[key], this.vueComponent.$route.query[key])) {
        return true;
      }
    }

    return false;
  }

  private createNewQuery(query: PlantRouteQuery): PlantRouteQuery {
    const newQuery: PlantRouteQuery = JSON.parse(JSON.stringify(this.vueComponent.$route.query));

    for (const key of Object.keys(query)) {
      newQuery[key] = query[key];
    }

    return newQuery;
  }

  private getRoute(query: PlantRouteQuery): RawLocation {
    return { 
      name: "Plant", 
      params: { id: this.vueComponent.plant.id },
      query: this.createNewQuery(query) as any,
    }
  }

  private isEqual(a: string | (string | null)[], b: string | (string | null)[]): boolean {
    if (!Array.isArray(a)) {
      a = [a];
    }
    if (!Array.isArray(b)) {
      b = [b];
    }

    a.sort();
    b.sort();

    if (a.length !== b.length) {
      return false;
    }

    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        return false;
      }
    }

    return true;
  }
}

export async function openCreateReportModalByQueryRoute(
  vueComponent: Vue,
  showCreateReport: () => void,
): Promise<boolean> {
  const query = vueComponent.$route.query as PlantRouteQuery;
  if (query.create_report !== "1") {
    return false;
  }

  await vueComponent.$nextTick();
  showCreateReport();

  const nextQuery = { ...(vueComponent.$route.query as PlantRouteQuery) };
  delete nextQuery.create_report;
  await vueComponent.$router.replace({ query: nextQuery });

  return true;
}
